import { default as accountExpirationWarning } from './accountExpirationWarning';
import { default as achievementExpirationOthers } from './achievementExpirationOthers';
import { default as achievementExpired } from './achievementExpired';
import { default as achievementExpiring } from './achievementExpiring';
import { default as achievementIndividualRequestDenied } from './achievementIndividualRequestDenied';
import { default as achievementIndividualRequestGranted } from './achievementIndividualRequestGranted';
import { default as achievementIndividualRequestNew } from './achievementIndividualRequestNew';
import { default as achievementRequestAchievementDenied } from './achievementRequestAchievementDenied';
import { default as achievementRequestAchievementGranted } from './achievementRequestAchievementGranted';
import { default as achievementRequestConversationNotesDenied } from './achievementRequestConversationNotesDenied';
import { default as achievementRequestConversationNotesGranted } from './achievementRequestConversationNotesGranted';
import { default as achievementRequestMilestoneDenied } from './achievementRequestMilestoneDenied';
import { default as achievementRequestMilestoneGranted } from './achievementRequestMilestoneGranted';
import { default as achievementRequestTaskListDenied } from './achievementRequestTaskListDenied';
import { default as achievementRequestTaskListGranted } from './achievementRequestTaskListGranted';
import { default as achievementSupervisorAddedToAchievement } from './achievementSupervisorAddedToAchievement';
import { default as achievementSupervisorAddedToConversationNotes } from './achievementSupervisorAddedToConversationNotes';
import { default as achievementSupervisorAddedToGroupConversation } from './achievementSupervisorAddedToGroupConversation';
import { default as achievementSupervisorAddedToMilestone } from './achievementSupervisorAddedToMilestone';
import { default as achievementSupervisorAddedToTaskList } from './achievementSupervisorAddedToTaskList';
import { default as calendarEventAccess } from './calendarEventAccess';
import { default as calendarEventAccessRequestAccept } from './calendarEventAccessRequestAccept';
import { default as calendarEventAccessRequestDeny } from './calendarEventAccessRequestDeny';
import { default as calendarEventCanceled } from './calendarEventCanceled';
import { default as calendarEventFeedbackRequest } from './calendarEventFeedbackRequest';
import { default as calendarEventFillFromQueue } from './calendarEventFillFromQueue';
import { default as calendarEventParticipation } from './calendarEventParticipation';
import { default as calendarEventRegistrationConfirmation } from './calendarEventRegistrationConfirmation';
import { default as calendarEventRegistrationConfirmationForOthers } from './calendarEventRegistrationConfirmationForOthers';
import { default as calendarEventRegistrationQueued } from './calendarEventRegistrationQueued';
import { default as calendarEventRegistrationQueuedForOthers } from './calendarEventRegistrationQueuedForOthers';
import { default as calendarEventSettingsChanged } from './calendarEventSettingsChanged';
import { default as completionExpirationOthers } from './completionExpirationOthers';
import { default as completionExpired } from './completionExpired';
import { default as completionExpiring } from './completionExpiring';
import { default as courseAccess } from './courseAccess';
import { default as courseAccessRequestAccept } from './courseAccessRequestAccept';
import { default as courseAccessRequestDeny } from './courseAccessRequestDeny';
import { default as courseCompleted } from './courseCompleted';
import { default as courseDeadlineApproaching } from './courseDeadlineApproaching';
import { default as courseDeadlineEmailList } from './courseDeadlineEmailList';
import { default as courseDeadlinePassed } from './courseDeadlinePassed';
import { default as courseSummary } from './courseSummary';
import { default as customEmail } from './customEmail';
import { default as groupInvite } from './groupInvite';
import { default as newUser } from './newUser';
import { default as openQuestionComment } from './openQuestionComment';
import { default as openQuestionRequestChanges } from './openQuestionRequestChanges';
import { default as openQuestionReview } from './openQuestionReview';
import { default as pathCompleted } from './pathCompleted';
import { default as pathDeadlineApproaching } from './pathDeadlineApproaching';
import { default as pathDeadlineEmailList } from './pathDeadlineEmailList';
import { default as pathDeadlineExpired } from './pathDeadlineExpired';
import { default as pathInvite } from './pathInvite';
import { default as pathSummary } from './pathSummary';
import { default as profileSummaryEmail } from './profileSummaryEmail';
import { default as register } from './register';
import { default as resetPassword } from './resetPassword';
import { default as supervisorSummary } from './supervisorSummary';
import { default as userCustomAttributesNotification } from './userCustomAttributesNotification';

import { getDefaultPlaceholders } from '..';

import type {
  EmailMessagePlaceholder,
  EmailTypeCategory,
  EmailTypeData,
  EmailMessageTypeEditable,
  LocaleMessages,
  Locales,
  ServerConfig
} from 'shared-types';

export type TemplateData = EmailTypeData;

export type TemplateParams = {
  defaultPlaceholders: { [key: string]: EmailMessagePlaceholder };
  language: Locales;
  messages: LocaleMessages;
  serverConfig: Pick<
    ServerConfig,
    | 'adminUrl'
    | 'authEnableLocalLogin'
    | 'emailDefaultReplyTo'
    | 'emailHidePasswordResetInfo'
    | 'hideAchievementsFromUI'
    | 'hideCalendarEventsFromUI'
    | 'hideCoursesFromUI'
    | 'hidePathsFromUI'
    | 'serverName'
    | 'serverNameTranslations'
  >;
};

// Types related to hidden products are omitted, so a stored template.type may not be present
type EmailTypesData = Partial<Record<EmailMessageTypeEditable, EmailTypeData>>;

// overloads
function getEmailTypesData(
  params: Pick<TemplateParams, 'language' | 'messages' | 'serverConfig'>
): EmailTypesData;

function getEmailTypesData(
  params: Pick<TemplateParams, 'language' | 'messages' | 'serverConfig'> & {
    type: EmailMessageTypeEditable;
  }
): EmailTypeData;

function getEmailTypesData(
  params: Pick<TemplateParams, 'language' | 'messages' | 'serverConfig'> & {
    type?: EmailMessageTypeEditable;
  }
) {
  const defaultPlaceholders = getDefaultPlaceholders({
    language: params.language,
    messages: params.messages,
    serverConfig: params.serverConfig
  });

  const templateParams = { ...params, defaultPlaceholders };

  const handlers = {
    'account-expiration-warning': accountExpirationWarning,
    'achievement-expiration-others': achievementExpirationOthers,
    'achievement-expired': achievementExpired,
    'achievement-expiring': achievementExpiring,
    'achievement-individual-request-denied': achievementIndividualRequestDenied,
    'achievement-individual-request-granted':
      achievementIndividualRequestGranted,
    'achievement-individual-request-new': achievementIndividualRequestNew,
    'achievement-request-achievement-denied':
      achievementRequestAchievementDenied,
    'achievement-request-achievement-granted':
      achievementRequestAchievementGranted,
    'achievement-request-conversation-notes-denied':
      achievementRequestConversationNotesDenied,
    'achievement-request-conversation-notes-granted':
      achievementRequestConversationNotesGranted,
    'achievement-request-milestone-denied': achievementRequestMilestoneDenied,
    'achievement-request-milestone-granted': achievementRequestMilestoneGranted,
    'achievement-request-task-list-denied': achievementRequestTaskListDenied,
    'achievement-request-task-list-granted': achievementRequestTaskListGranted,
    'achievement-supervisor-added-to-achievement':
      achievementSupervisorAddedToAchievement,
    'achievement-supervisor-added-to-conversation-notes':
      achievementSupervisorAddedToConversationNotes,
    'achievement-supervisor-added-to-group-conversation':
      achievementSupervisorAddedToGroupConversation,
    'achievement-supervisor-added-to-milestone':
      achievementSupervisorAddedToMilestone,
    'achievement-supervisor-added-to-task-list':
      achievementSupervisorAddedToTaskList,
    'calendar-event-access': calendarEventAccess,
    'calendar-event-access-request-accept': calendarEventAccessRequestAccept,
    'calendar-event-access-request-deny': calendarEventAccessRequestDeny,
    'calendar-event-canceled': calendarEventCanceled,
    'calendar-event-feedback-request': calendarEventFeedbackRequest,
    'calendar-event-fill-from-queue': calendarEventFillFromQueue,
    'calendar-event-participation': calendarEventParticipation,
    'calendar-event-registration-confirmation':
      calendarEventRegistrationConfirmation,
    'calendar-event-registration-confirmation-for-others':
      calendarEventRegistrationConfirmationForOthers,
    'calendar-event-registration-queued': calendarEventRegistrationQueued,
    'calendar-event-registration-queued-for-others':
      calendarEventRegistrationQueuedForOthers,
    'calendar-event-settings-changed': calendarEventSettingsChanged,
    'completion-expired': completionExpired,
    'completion-expiring': completionExpiring,
    'completion-expiration-others': completionExpirationOthers,
    'course-access': courseAccess,
    'course-access-request-accept': courseAccessRequestAccept,
    'course-access-request-deny': courseAccessRequestDeny,
    'course-completed': courseCompleted,
    'course-deadline-approaching': courseDeadlineApproaching,
    'course-deadline-email-list': courseDeadlineEmailList,
    'course-deadline-passed': courseDeadlinePassed,
    'course-summary': courseSummary,
    'custom-email': customEmail,
    'group-invite': groupInvite,
    'new-user': newUser,
    'open-question-comment': openQuestionComment,
    'open-question-request-changes': openQuestionRequestChanges,
    'open-question-review': openQuestionReview,
    'path-completed': pathCompleted,
    'path-deadline-approaching': pathDeadlineApproaching,
    'path-deadline-email-list': pathDeadlineEmailList,
    'path-deadline-expired': pathDeadlineExpired,
    'path-invite': pathInvite,
    'path-summary': pathSummary,
    'profile-summary-email': profileSummaryEmail,
    register: register,
    'reset-password': resetPassword,
    'supervisor-summary': supervisorSummary,
    'user-custom-attributes-notification': userCustomAttributesNotification
  };

  if (params.type) {
    return handlers[params.type](templateParams);
  }

  const data: EmailTypesData = {};

  Object.keys(handlers).forEach((type) => {
    const currentType = type as EmailMessageTypeEditable;
    const emailData = handlers[currentType](templateParams);

    if (
      isEmailVisible({
        category: emailData.category,
        serverConfig: params.serverConfig
      })
    ) {
      data[currentType] = emailData;
    }
  });

  return data;
}

const isEmailVisible = ({
  category,
  serverConfig
}: {
  category: EmailTypeCategory;
  serverConfig: Pick<
    ServerConfig,
    | 'hideAchievementsFromUI'
    | 'hideCalendarEventsFromUI'
    | 'hideCoursesFromUI'
    | 'hidePathsFromUI'
  >;
}) => {
  switch (category) {
    case 'achievements':
      return !serverConfig.hideAchievementsFromUI;
    case 'calendar-events':
      return !serverConfig.hideCalendarEventsFromUI;
    case 'courses':
      return !serverConfig.hideCoursesFromUI;
    case 'paths':
      return !serverConfig.hidePathsFromUI;
    default:
      return true;
  }
};

export default getEmailTypesData;
